import React, { FC, useState } from 'react';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import PortalHeader from '../../assessment-portal/components/portal-header';
import { TABLE_TIME } from '../../constants/time-formats';
import type { IdentityConfirmValues } from '../types/flow';

interface IdentityConfirmPageProps {
  initialValues?: IdentityConfirmValues
  submitting?: boolean
  errorMessage?: string
  onBack: () => void
  onSubmit: (values: IdentityConfirmValues) => void
}

const PersonIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="3.8" />
    <path d="M4.5 20.5c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

const parseDobValue = (value: string): Moment | undefined => {
  if (!value) return undefined;
  const parsed = moment(value, TABLE_TIME, true);
  return parsed.isValid() ? parsed : undefined;
};

const IdentityConfirmPage: FC<IdentityConfirmPageProps> = ({
  initialValues,
  submitting = false,
  errorMessage,
  onBack,
  onSubmit,
}) => {
  const [firstName, setFirstName] = useState(initialValues?.firstName ?? '');
  const [lastName, setLastName] = useState(initialValues?.lastName ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(initialValues?.dateOfBirth ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !dateOfBirth.trim()) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: dateOfBirth.trim(),
    });
  };

  const isFormValid = Boolean(firstName.trim() && lastName.trim() && dateOfBirth.trim());

  return (
    <div className="adhd-portal adhd-submit-page min-h-screen flex flex-col bg-page">
      <PortalHeader />
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="adhd-submit-shell">
          <div className="adhd-submit-hero">
            <div className="adhd-submit-hero-icon">
              <PersonIcon />
            </div>
            <h1 className="adhd-submit-hero-title">
              Last step — confirm it&apos;s you
            </h1>
            <p className="adhd-submit-hero-subtitle">
              So we save your answers to the right booking. Takes a few seconds.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="adhd-submit-form-card">
              <div className="adhd-submit-field">
                <p id="firstName-label" className="adhd-submit-label">First name</p>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  aria-labelledby="firstName-label"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. John"
                  required
                  className="adhd-submit-input"
                />
              </div>

              <div className="adhd-submit-field">
                <p id="lastName-label" className="adhd-submit-label">Last name</p>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  aria-labelledby="lastName-label"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="e.g. Lee"
                  required
                  className="adhd-submit-input"
                />
              </div>

              <div className="adhd-submit-field adhd-submit-field--last">
                <p id="dateOfBirth-label" className="adhd-submit-label">Date of birth</p>
                <DatePicker
                  id="dateOfBirth"
                  aria-labelledby="dateOfBirth-label"
                  className="adhd-submit-datepicker"
                  value={parseDobValue(dateOfBirth)}
                  onChange={(date) => setDateOfBirth(date ? date.format(TABLE_TIME) : '')}
                  format={TABLE_TIME}
                  placeholder="DD/MM/YYYY"
                  inputReadOnly
                  disabled={submitting}
                  disabledDate={(current) => Boolean(current && current > moment().endOf('day'))}
                />
              </div>

              <div className="adhd-submit-privacy">
                <LockIcon />
                Used only to match your answers — kept private.
              </div>
            </div>

            {errorMessage && (
              <p className="adhd-submit-error" role="alert">
                {errorMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !isFormValid}
              className="adhd-submit-cta"
            >
              {submitting ? 'Submitting…' : 'Submit my answers'}
              {!submitting && <CheckIcon />}
            </button>
          </form>

          <button
            type="button"
            onClick={onBack}
            className="adhd-submit-back"
          >
            <BackArrowIcon />
            Back to questionnaire
          </button>
        </div>
      </main>
    </div>
  );
};

export default IdentityConfirmPage;
